"use client";

import * as React from "react";
import type { Column, Header } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Button } from "../ui/button";
import { TableHead } from "../ui/table";
import { cn } from "../../lib/utils";

// Shared sortable column header for the admin data grids (sc-100769). Mirrors the
// `createActionsColumn` extraction precedent: a page declares its columns, this renders the control.
//
//   { accessorKey: "id", header: ({ column }) => <SortableHeader column={column} label="Job" /> }
//
// Sortability is TanStack's own per-column flag — `getCanSort()` is
// `enableSorting ?? true && !!column.accessorFn` — so there is no new API to learn and no per-page
// branching: a display column with no accessor renders as a plain label automatically.

/** `aria-sort` for a header cell: the sorted column's direction, or `undefined` (attribute omitted). */
export function ariaSortOf<TData, TValue>(
  column: Column<TData, TValue>
): "ascending" | "descending" | undefined {
  if (!column.getCanSort()) return undefined;
  const direction = column.getIsSorted();
  if (direction === "asc") return "ascending";
  if (direction === "desc") return "descending";
  return undefined;
}

/**
 * Column-header renderer.
 *
 * Sortable → a real `<button>` (keyboard reachable, Enter/Space-activatable natively) whose direction
 * is shown by a persistent non-color icon: `ArrowUp` ascending, `ArrowDown` descending, a muted
 * `ChevronsUpDown` when unsorted. The icon inherits `currentColor`, so it reads in both themes and adds
 * no token.
 *
 * Not sortable → the bare label, rendered exactly as an unadorned header is today.
 */
export function SortableHeader<TData, TValue>({
  column,
  label,
  className,
}: {
  column: Column<TData, TValue>;
  label: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  if (!column.getCanSort()) {
    return <span className={className}>{label}</span>;
  }

  const direction = column.getIsSorted();
  const Indicator = direction === "asc" ? ArrowUp : direction === "desc" ? ArrowDown : ChevronsUpDown;

  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn("-ml-2 h-8 px-2 font-medium", className)}
      onClick={column.getToggleSortingHandler()}
    >
      {label}
      <Indicator
        data-slot="sort-indicator"
        data-direction={direction || "none"}
        aria-hidden="true"
        className={cn("size-3.5", !direction && "text-muted-foreground")}
      />
    </Button>
  );
}

/**
 * `<th>` wrapper carrying `aria-sort`. Replaces the hand-rolled `TableHead` + `flexRender` block, so a
 * page's header row is one component and every header goes through `SortableHeader`.
 */
export function SortableTableHead<TData, TValue>({
  header,
  className,
}: {
  header: Header<TData, TValue>;
  className?: string;
}): React.JSX.Element {
  return (
    <TableHead className={className} aria-sort={ariaSortOf(header.column)}>
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
    </TableHead>
  );
}
